import React, { Component } from 'react'
import { FormattedMessage, injectIntl, InjectedIntlProps } from 'react-intl'
import { observer } from 'mobx-react'
import { autobind } from 'core-decorators'
import { IoIosClose } from 'react-icons/io'
import classNames from 'classnames'
import PerfectScrollbar from 'perfect-scrollbar'

import ReviewCenterSingleChat from './ReviewCenterSingleChat'
import Screenshot from './Screenshot'
import GenevaButton from '../../shared/components/GenevaButton'
import ScreenshotDialog from './modals/ScreenshotDialog'
import { deepGet } from '../../shared/obj'
import ContentLoadingBox from '../../shared/components/ContentLoadingBox'

import { IReviewModel } from '../../models'
import { IApplicationStore, IReviewStore } from '../../stores'
import './ReviewCenter.scss'


interface IReviewCenterChatProps extends InjectedIntlProps {
  reviewModel: IReviewModel,
  reviewStore: IReviewStore,
  applicationStore: IApplicationStore
}

interface IState {
  displayedUnknown: boolean,
  fullChat: boolean,
  showScreenshotDialog: boolean,
  screenshot: {
    id: number,
    url: string,
    text: string
  }
}

@observer
class ReviewCenterChat extends Component<IReviewCenterChatProps> {
  state: IState
  ps: any

  constructor(props) {
    super(props)
    this.state = {
      fullChat: false,
      showScreenshotDialog: false,
      screenshot: {
        id: null,
        url: '',
        text: ''
      },
      displayedUnknown: false
    }
  }

  componentDidMount() {
    this.ps = new PerfectScrollbar('#sidebar-container')
  }

  @autobind
  handleAnswer() {
    this.props.reviewStore.sendMessage()
    this.props.reviewStore.resetActiveComment()

    this.setState({
      fullChat: false
    })
  }

  @autobind
  handleActionChange({ target }) {
    this.props.reviewStore.activeComment.action = (target.value * 1)
  }

  @autobind
  handleTextChange({ target }) {
    this.props.reviewStore.activeComment.text = target.value
  }

  @autobind
  handleChatToggle() {
    const toggle = this.state.fullChat
    this.setState({
      fullChat: !toggle
    })
  }

  @autobind
  handleClearAndToggle() {
    this.props.reviewStore.resetActiveComment()
    this.handleChatToggle()
  }

  @autobind
  handleThumbnailClick({ target }) {
    // Save call since there is nothing to click on if array is empty
    const screenshot = this.props.reviewStore.activeComment.screenshots.find(el => el.id === target.id * 1)

    this.setState({
      screenshot,
      showScreenshotDialog: true
    })
  }

  @autobind
  handleDeleteScreenshot() {
    this.props.reviewStore.deleteImage(this.state.screenshot.id)
    this.setState({
      showScreenshotDialog: false
    })
  }

  @autobind
  handleSaveScreenshot(data) {
    this.props.reviewStore.saveImage(this.state.screenshot.id, data)
    this.setState({
      showScreenshotDialog: false
    })
  }

  @autobind
  handleIframeReload() {
    this.props.applicationStore.requestIframeUpdate = true
  }

  renderAnswer() {
    const { formatMessage } = this.props.intl
    const { reviewStore, applicationStore } = this.props
    const { activeComment } = this.props.reviewStore

    const textarea = document.getElementById('rcAnswerText')
    if (textarea && !textarea.oninput) {
      textarea.oninput = () => {
        textarea.style.height = '' /* Reset the height */
        textarea.style.height = `${textarea.scrollHeight}px`
      }
    }

    if (this.state.fullChat) {
      const currentUser = deepGet(reviewStore, 'store.applicationStore.userName', 'Unknown')

      return (
        <div className="rc-answer">
          <div className="rc-name">
            {currentUser}
            <IoIosClose
              size={29}
              onClick={this.handleClearAndToggle}
            />
          </div>
          <textarea
            autoFocus
            cols={35}
            rows={4}
            id="rcAnswerText"
            name="answer-text"
            maxLength={1500}
            defaultValue={activeComment.text}
            onChange={this.handleTextChange}
          />
          <div className="rc-answer-options">
            <select
              className="rc-action-dropdown"
              onChange={this.handleActionChange}
              value={activeComment.action}
            >
              <option
                key="action-none"
                value={0}
              >
                {formatMessage({ id: 'review-center.action.none' })}
              </option>
              <option
                key="action-approved"
                value={1}
              >
                {formatMessage({ id: 'review-center.action.approved' })}
              </option>
              <option
                key="action-review"
                value={2}
              >
                {formatMessage({ id: 'review-center.action.review' })}
              </option>
            </select>
            <GenevaButton
              className="small button"
              onClick={this.handleAnswer}
            >
              <FormattedMessage id="button.send" />
            </GenevaButton>
          </div>
          <div className="thumbnails">
            {
              reviewStore.activeComment.screenshots.map((screenshot) => {
                return (
                  <div
                    className="single-thumbnail"
                    key={`${screenshot.id}`}
                    id={`${screenshot.id}`}
                    onClick={this.handleThumbnailClick}
                  >
                    <img id={`${screenshot.id}`} src={screenshot.thumbnailUrl} alt="" />
                  </div>
                )
              })}
            <div
              className={classNames('single-thumbnail saving', { hidden: !reviewStore.savingImage })}
            >
              <ContentLoadingBox
                spinner
                className="thumbnailLoader"
              />
            </div>
          </div>
          <hr />
          <div className="rc-create-screenshot">
            <Screenshot
              applicationStore={applicationStore}
              reviewStore={reviewStore}
            />
            <FormattedMessage id="review-center.chat.click" />
          </div>
        </div>

      )

    }

    return (
      <div className="rc-answer">
        <label htmlFor="answer-text">
          <FormattedMessage id="review-center.chat.answer" />
        </label>
        <input
          type="text"
          id="answer-text"
          name="answer-text"
          onClick={this.handleChatToggle}
        />
      </div>
    )
  }

  @autobind
  renderChats() {
    const { reviewModel, reviewStore } = this.props

    if (!reviewModel) {
      return null
    }

    return (
      <React.Fragment>
        <div className={classNames('rc-chat-block', { hidden: !reviewStore.sendingMessage })}>
          <ContentLoadingBox
            spinner
            className="singleChatLoader"
          />
        </div>
        {reviewModel.comments.map((chat) => {
          // for sessions left open for a long time, multiple unknown errors would be shown
          // this is to keep the unknown messages from looking messy, only 1 needed
          if (chat.name === 'Unknown') {
            if (this.state.displayedUnknown) {
              return null
            }
            this.setState({
              displayedUnknown: true
            })
          }
          return (
            <ReviewCenterSingleChat
              chat={chat}
              key={chat.id}
              currentUser={this.props.applicationStore.userName}
              reviewModel={reviewModel}
            />
          )
        })}
      </React.Fragment>
    )
  }

  renderScreenshotDialog() {
    const { screenshot } = this.state

    return (
      <ScreenshotDialog
        show
        text={screenshot.text}
        imageURL={screenshot.url}
        handleClose={this.handleDeleteScreenshot}
        handleConfirm={this.handleSaveScreenshot}
      />
    )

  }

  renderWarning() {
    const applicationStore = this.props.applicationStore

    if (applicationStore.pageChanged) {
      return (
        <div className="sidebar-warning">
          <FormattedMessage id="sidebar.pageChanged" />
          <div
            className="update-iframe"
            onClick={this.handleIframeReload}
          >
            <FormattedMessage id="sidebar.reload-iframe" />
          </div>
        </div>
      )
    }
    return null
  }

  render() {

    if (this.ps) {
      this.ps.update()
    }

    return (
      <div className="rc-chat" id="sidebar-container">
        {this.renderAnswer()}
        {this.renderWarning()}
        {this.renderChats()}

        {this.state.showScreenshotDialog
          ? this.renderScreenshotDialog()
          : null}

      </div>
    )
  }
}

export default injectIntl(ReviewCenterChat)
